import React from "react";
import ReactDOM from "react-dom";
import Icon from "../OldFront/Simple/IconComponent/Icon";
import { ImageGalleryThumbnails } from "./ImageGalleryThumbnails";
import { IImages, preloadImagesAround, wrapImageIndex } from "./imageGalleryImages";

const SWIPE_MINIMUM_PX = 50;
const AXIS_LOCK_PX = 10;

interface IImageGalleryLightbox {
    images: Array<IImages>;
    currentImageIndex: number;
    onSelectImage: (index: number) => void;
    onClose: () => void;
}

interface IImageGalleryLightboxState {
    imageIsLoaded: boolean;
}

export class ImageGalleryLightbox extends React.Component<React.PropsWithChildren<IImageGalleryLightbox>, IImageGalleryLightboxState> {
    private dialogRef = React.createRef<HTMLDivElement>();
    private closeButtonRef = React.createRef<HTMLButtonElement>();
    private elementFocusedBeforeOpen: HTMLElement | undefined;
    private previousBodyOverflow = "";
    private previousBodyPaddingRight = "";
    private touchOrigin: { x: number, y: number } | undefined;
    private touchAxis: "undecided" | "horizontal" | "vertical" = "undecided";
    private lastGestureWasASwipe = false;

    constructor(props: IImageGalleryLightbox) {
        super(props);
        this.state = {
            imageIsLoaded: false,
        };
    }

    public componentDidMount(): void {
        this.elementFocusedBeforeOpen = document.activeElement instanceof HTMLElement
            ? document.activeElement
            : undefined;
        document.addEventListener("keydown", this.onKeyDown);
        this.lockPageScroll();
        preloadImagesAround(this.props.images, this.props.currentImageIndex);

        if (this.closeButtonRef.current !== null) {
            this.closeButtonRef.current.focus();
        }
    }

    public componentDidUpdate(previousProps: IImageGalleryLightbox): void {
        if (previousProps.currentImageIndex !== this.props.currentImageIndex) {
            preloadImagesAround(this.props.images, this.props.currentImageIndex);
        }
    }

    public componentWillUnmount(): void {
        document.removeEventListener("keydown", this.onKeyDown);
        this.unlockPageScroll();

        if (this.elementFocusedBeforeOpen !== undefined && document.contains(this.elementFocusedBeforeOpen)) {
            this.elementFocusedBeforeOpen.focus();
        }
    }

    public render (): JSX.Element | null {
        const image = this.props.images[this.props.currentImageIndex];

        if (typeof document === "undefined" || image === undefined) {
            return null;
        }

        const hasMultipleImages = this.props.images.length > 1;

        return ReactDOM.createPortal(
            <div
                className="imageGalleryLightbox"
                ref={this.dialogRef}
                role="dialog"
                aria-modal={true}
                aria-label="Property photos"
            >
                <div className="imageGalleryLightbox__bar">
                    <div className="imageGalleryLightbox__counter">
                        <Icon iconName="camera" height={2} width={2} type="mouse" />
                        <span className="imageGalleryLightbox__counter-text">
                            {`${this.props.currentImageIndex + 1} / ${this.props.images.length}`}
                        </span>
                    </div>
                    <button
                        type="button"
                        className="imageGalleryLightbox__close"
                        ref={this.closeButtonRef}
                        onClick={this.props.onClose}
                        aria-label="Close photos"
                    >
                        <Icon iconName="cross" height={1.8} width={1.8} type="mouse" />
                        <span className="imageGalleryLightbox__close-text">Close</span>
                    </button>
                </div>

                {/* Only a click that lands on the backdrop itself closes — a click that
                    lands on the photo is someone looking at the photo. */}
                <div
                    className="imageGalleryLightbox__stage"
                    onClick={this.onStageClick}
                    onTouchStart={this.onTouchStart}
                    onTouchMove={this.onTouchMove}
                    onTouchEnd={this.onTouchEnd}
                    onTouchCancel={this.onTouchCancel}
                >
                    <img
                        key={image.src}
                        className={
                            "imageGalleryLightbox__image" +
                            (this.state.imageIsLoaded ? " imageGalleryLightbox__image--loaded" : "")
                        }
                        src={image.src}
                        alt={image.alt}
                        draggable={false}
                        onLoad={() => this.setState({ imageIsLoaded: true })}
                        onError={() => this.setState({ imageIsLoaded: true })}
                    />

                    {hasMultipleImages &&
                        <button
                            type="button"
                            className="imageGalleryLightbox__nav imageGalleryLightbox__nav--previous"
                            onClick={this.goToPreviousImage}
                            aria-label="Previous photo"
                        >
                            <Icon iconName="leftArrow" height={2.6} width={2.6} type="mouse" />
                        </button>
                    }
                    {hasMultipleImages &&
                        <button
                            type="button"
                            className="imageGalleryLightbox__nav imageGalleryLightbox__nav--next"
                            onClick={this.goToNextImage}
                            aria-label="Next photo"
                        >
                            <Icon iconName="rightArrow" height={2.6} width={2.6} type="mouse" />
                        </button>
                    }
                </div>

                {hasMultipleImages &&
                    <ImageGalleryThumbnails
                        images={this.props.images}
                        currentImageIndex={this.props.currentImageIndex}
                        size="lightbox"
                        onSelectImage={this.selectImage}
                    />
                }
            </div>,
            document.body,
        );
    }

    private selectImage = (index: number): void => {
        if (index === this.props.currentImageIndex) {
            return;
        }

        this.setState({ imageIsLoaded: false });
        this.props.onSelectImage(index);
    };

    private goToOffset = (offset: number): void =>
        this.selectImage(wrapImageIndex(this.props.currentImageIndex + offset, this.props.images.length));

    private goToPreviousImage = (): void => this.goToOffset(-1);

    private goToNextImage = (): void => this.goToOffset(1);

    private onStageClick = (e: React.MouseEvent<HTMLDivElement>): void => {
        if (this.lastGestureWasASwipe) {
            this.lastGestureWasASwipe = false;
            return;
        }

        if (e.target === e.currentTarget) {
            this.props.onClose();
        }
    };

    private onKeyDown = (e: KeyboardEvent): void => {
        switch (e.key) {
            case "Escape":
                e.preventDefault();
                this.props.onClose();
                return;
            case "ArrowLeft":
                e.preventDefault();
                this.goToPreviousImage();
                return;
            case "ArrowRight":
                e.preventDefault();
                this.goToNextImage();
                return;
            case "Home":
                e.preventDefault();
                this.selectImage(0);
                return;
            case "End":
                e.preventDefault();
                this.selectImage(this.props.images.length - 1);
                return;
            case "Tab":
                this.keepFocusInsideDialog(e);
                return;
            default:
                return;
        }
    };

    private keepFocusInsideDialog = (e: KeyboardEvent): void => {
        const dialog = this.dialogRef.current;

        if (dialog === null) {
            return;
        }

        const focusable = Array.from(dialog.querySelectorAll<HTMLButtonElement>("button:not([disabled])"));

        if (focusable.length === 0) {
            return;
        }

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;
        const focusIsOutside = !(active instanceof HTMLElement) || !dialog.contains(active);

        if (e.shiftKey && (focusIsOutside || active === first)) {
            e.preventDefault();
            last.focus();
            return;
        }

        if (!e.shiftKey && (focusIsOutside || active === last)) {
            e.preventDefault();
            first.focus();
        }
    };

    private onTouchStart = (e: React.TouchEvent): void => {
        const touch = e.touches[0];
        this.touchOrigin = { x: touch.clientX, y: touch.clientY };
        this.touchAxis = "undecided";
        this.lastGestureWasASwipe = false;
    };

    private onTouchMove = (e: React.TouchEvent): void => {
        if (this.touchOrigin === undefined || this.touchAxis !== "undecided") {
            return;
        }

        const touch = e.touches[0];
        const dx = touch.clientX - this.touchOrigin.x;
        const dy = touch.clientY - this.touchOrigin.y;

        if (Math.abs(dx) < AXIS_LOCK_PX && Math.abs(dy) < AXIS_LOCK_PX) {
            return;
        }

        this.touchAxis = Math.abs(dx) > Math.abs(dy) ? "horizontal" : "vertical";
        this.lastGestureWasASwipe = this.touchAxis === "horizontal";
    };

    private onTouchEnd = (e: React.TouchEvent): void => {
        const origin = this.touchOrigin;
        const axis = this.touchAxis;
        this.onTouchCancel();

        if (origin === undefined || axis !== "horizontal") {
            return;
        }

        const dx = e.changedTouches[0].clientX - origin.x;

        if (dx <= -SWIPE_MINIMUM_PX) {
            this.goToNextImage();
            return;
        }

        if (dx >= SWIPE_MINIMUM_PX) {
            this.goToPreviousImage();
        }
    };

    private onTouchCancel = (): void => {
        this.touchOrigin = undefined;
        this.touchAxis = "undecided";
    };

    private lockPageScroll = (): void => {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        this.previousBodyOverflow = document.body.style.overflow;
        this.previousBodyPaddingRight = document.body.style.paddingRight;
        document.body.style.overflow = "hidden";

        if (scrollbarWidth > 0) {
            document.body.style.paddingRight = `${scrollbarWidth}px`;
        }
    };

    private unlockPageScroll = (): void => {
        document.body.style.overflow = this.previousBodyOverflow;
        document.body.style.paddingRight = this.previousBodyPaddingRight;
    };
}
